import { useState, useEffect, useRef } from 'react';
import { LogOut, Users, X, Shield, Mail, Send, Clock, Factory, Check, ChevronDown, Plus, Pencil, KeyRound, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useTeamMembers, useRemoveUser, useInviteUser, useInvitations, useRevokeInvitation, useCompanyIndustries, useUpdateIndustries, useUpdateUser, useResetUserPassword, useResendInvitation, useToggleSuperAdmin } from '@/hooks/useTeam';
import { Card, Button, Badge, Input } from '@/components/ui';
import { useT, useLanguage, type Lang } from '@/i18n/LanguageContext';

const INDUSTRY_OPTIONS = [
  'Automotive',
  'Chemicals',
  'Construction & Building Materials',
  'Consumer Electronics',
  'Cosmetics & Beauty',
  'Energy & Power',
  'Fashion & Apparel',
  'Finance & Banking',
  'Food & Beverage',
  'Furniture & Home Goods',
  'Healthcare & Medical Devices',
  'Industrial Manufacturing',
  'Information Technology',
  'Logistics & Supply Chain',
  'Metals & Mining',
  'Packaging & Printing',
  'Pharmaceuticals',
  'Plastics & Rubber',
  'Retail & E-Commerce',
  'Semiconductors',
  'Telecommunications',
  'Textiles & Fabrics',
];

export default function SettingsPage() {
  const { user, company, logout } = useAuthStore();
  const { lang, setLang } = useLanguage();
  const { data: team, isLoading: teamLoading } = useTeamMembers();
  const removeUser = useRemoveUser();
  const inviteUser = useInviteUser();
  const { data: invitations } = useInvitations();
  const revokeInvitation = useRevokeInvitation();
  const updateUser = useUpdateUser();
  const resetPassword = useResetUserPassword();
  const resendInvitation = useResendInvitation();
  const toggleSuperAdmin = useToggleSuperAdmin();
  const { data: companyData } = useCompanyIndustries();
  const updateIndustries = useUpdateIndustries();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editRole, setEditRole] = useState('');
  const [resetSentId, setResetSentId] = useState<string | null>(null);
  const [resentInviteId, setResentInviteId] = useState<string | null>(null);
  const t = useT();

  // Industry state
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');
  const [customIndustry, setCustomIndustry] = useState('');
  const [industriesSaved, setIndustriesSaved] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.role === 'admin';

  // Sync industries from server
  useEffect(() => {
    if (companyData?.industries) {
      setSelectedIndustries(companyData.industries);
    }
  }, [companyData?.industries]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIndustriesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasIndustryChanges = JSON.stringify(selectedIndustries.sort()) !== JSON.stringify((companyData?.industries ?? []).sort());

  const toggleIndustry = (industry: string) => {
    setSelectedIndustries((prev) =>
      prev.includes(industry) ? prev.filter((i) => i !== industry) : [...prev, industry],
    );
  };

  const addCustomIndustry = () => {
    const trimmed = customIndustry.trim();
    if (trimmed && !selectedIndustries.includes(trimmed)) {
      setSelectedIndustries((prev) => [...prev, trimmed]);
      setCustomIndustry('');
    }
  };

  const handleSaveIndustries = async () => {
    try {
      await updateIndustries.mutateAsync(selectedIndustries);
      setIndustriesSaved(true);
      setTimeout(() => setIndustriesSaved(false), 3000);
    } catch {
      // error handled by mutation
    }
  };

  const filteredOptions = INDUSTRY_OPTIONS.filter(
    (opt) =>
      opt.toLowerCase().includes(industrySearch.toLowerCase()) &&
      !selectedIndustries.includes(opt),
  );

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await inviteUser.mutateAsync(inviteEmail.trim());
      setInviteSent(true);
      setInviteEmail('');
      setTimeout(() => setInviteSent(false), 3000);
    } catch {
      // error handled by mutation
    }
  };

  const handleLogout = () => {
    if (window.confirm(t.common.confirmSignOut)) {
      logout();
    }
  };

  const handleRemoveUser = (userId: string, name: string) => {
    if (window.confirm(t.settings.confirmRemove.replace('{name}', name))) {
      removeUser.mutate(userId);
    }
  };

  const handleRevokeInvite = (invId: string, email: string) => {
    if (window.confirm(t.settings.confirmRevoke.replace('{email}', email))) {
      revokeInvitation.mutate(invId);
    }
  };

  const startEditUser = (member: { id: string; fullName: string; phone: string; role: string }) => {
    setEditingUserId(member.id);
    setEditName(member.fullName);
    setEditPhone(member.phone);
    setEditRole(member.role);
  };

  const handleSaveUser = () => {
    if (!editingUserId) return;
    updateUser.mutate(
      { userId: editingUserId, fullName: editName.trim(), phone: editPhone.trim(), role: editRole },
      { onSuccess: () => setEditingUserId(null) },
    );
  };

  const handleResetPassword = (userId: string, email: string) => {
    if (!window.confirm(`Send password reset email to ${email}?`)) return;
    resetPassword.mutate(userId, {
      onSuccess: () => {
        setResetSentId(userId);
        setTimeout(() => setResetSentId(null), 3000);
      },
    });
  };

  const handleResendInvite = (invId: string) => {
    resendInvitation.mutate(invId, {
      onSuccess: () => {
        setResentInviteId(invId);
        setTimeout(() => setResentInviteId(null), 3000);
      },
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-lg mx-auto space-y-4">
      <h1 className="text-xl font-bold text-reevol-text">{t.settings.title}</h1>

      {/* Profile */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-reevol-text mb-3">{t.settings.profile}</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-reevol-text-muted">{t.common.name}</span>
            <span className="text-reevol-text font-medium">{user?.fullName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-reevol-text-muted">{t.common.email}</span>
            <span className="text-reevol-text font-medium">{user?.email}</span>
          </div>
          {user?.phone && (
            <div className="flex justify-between">
              <span className="text-reevol-text-muted">{t.common.phone}</span>
              <span className="text-reevol-text font-medium">{user.phone}</span>
            </div>
          )}
          {user?.wechat && (
            <div className="flex justify-between">
              <span className="text-reevol-text-muted">{t.settings.wechat}</span>
              <span className="text-reevol-text font-medium">{user.wechat}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-reevol-text-muted">{t.settings.role}</span>
            <span className="text-reevol-text font-medium capitalize">{user?.role}</span>
          </div>
        </div>
      </Card>

      {/* Company */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-reevol-text mb-3">{t.settings.company}</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-reevol-text-muted">{t.settings.company}</span>
            <span className="text-reevol-text font-medium">{company?.name}</span>
          </div>
        </div>
      </Card>

      {/* Industry Selection (admin only) */}
      {isAdmin && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Factory size={16} className="text-reevol-text-muted" />
            <h2 className="text-sm font-semibold text-reevol-text">{t.settings.yourIndustries}</h2>
          </div>
          <p className="text-xs text-reevol-text-muted mb-3">
            {t.settings.industryDesc}
          </p>

          {/* Selected industries as tags */}
          {selectedIndustries.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {selectedIndustries.map((ind) => (
                <span
                  key={ind}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-reevol-indigo/10 text-reevol-indigo rounded-full text-xs font-medium"
                >
                  {ind}
                  <button
                    onClick={() => toggleIndustry(ind)}
                    className="p-0.5 rounded-full hover:bg-reevol-indigo/20 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Dropdown trigger */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIndustriesOpen(!industriesOpen)}
              className="w-full flex items-center justify-between px-3 py-2 bg-reevol-input border border-reevol-border rounded-xl text-sm text-reevol-text-muted hover:border-reevol-border-hover transition-colors"
            >
              <span>{t.settings.addIndustry}</span>
              <ChevronDown size={14} className={`transition-transform ${industriesOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {industriesOpen && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-reevol-border rounded-xl shadow-lg max-h-64 overflow-hidden">
                {/* Search */}
                <div className="p-2 border-b border-reevol-border/50">
                  <input
                    type="text"
                    placeholder={t.settings.searchIndustries}
                    value={industrySearch}
                    onChange={(e) => setIndustrySearch(e.target.value)}
                    className="w-full px-3 py-1.5 bg-reevol-elevated border border-reevol-border rounded-lg text-sm text-reevol-text placeholder:text-reevol-text-muted focus:outline-none focus:ring-2 focus:ring-reevol-blue/30"
                    autoFocus
                  />
                </div>

                {/* Options */}
                <div className="overflow-y-auto max-h-40">
                  {filteredOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        toggleIndustry(opt);
                        setIndustrySearch('');
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-reevol-text hover:bg-reevol-elevated transition-colors text-left"
                    >
                      <div className="w-4 h-4 rounded border border-reevol-border flex items-center justify-center flex-shrink-0">
                        {selectedIndustries.includes(opt) && <Check size={10} className="text-reevol-indigo" />}
                      </div>
                      {opt}
                    </button>
                  ))}
                  {filteredOptions.length === 0 && !industrySearch && (
                    <p className="px-4 py-3 text-xs text-reevol-text-muted">{t.settings.allSelected}</p>
                  )}
                  {filteredOptions.length === 0 && industrySearch && (
                    <p className="px-4 py-3 text-xs text-reevol-text-muted">{t.settings.noMatches}</p>
                  )}
                </div>

                {/* Custom industry input */}
                <div className="p-2 border-t border-reevol-border/50">
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      placeholder={t.settings.customIndustry}
                      value={customIndustry}
                      onChange={(e) => setCustomIndustry(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomIndustry();
                        }
                      }}
                      className="flex-1 px-3 py-1.5 bg-reevol-elevated border border-reevol-border rounded-lg text-sm text-reevol-text placeholder:text-reevol-text-muted focus:outline-none focus:ring-2 focus:ring-reevol-blue/30"
                    />
                    <button
                      onClick={addCustomIndustry}
                      disabled={!customIndustry.trim()}
                      className="px-2 py-1.5 bg-reevol-elevated border border-reevol-border rounded-lg text-reevol-text-muted hover:text-reevol-indigo hover:border-reevol-border-hover transition-colors disabled:opacity-50"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save button */}
          {hasIndustryChanges && (
            <Button
              onClick={handleSaveIndustries}
              loading={updateIndustries.isPending}
              fullWidth
              className="mt-3"
            >
              {t.settings.saveIndustries}
            </Button>
          )}
          {industriesSaved && (
            <p className="text-xs text-emerald-600 mt-2">{t.settings.industriesSaved}</p>
          )}
          {updateIndustries.isError && (
            <p className="text-xs text-red-600 mt-2">
              {(updateIndustries.error as Error)?.message || t.settings.failedSaveIndustries}
            </p>
          )}
        </Card>
      )}

      {/* Non-admin: show industries read-only */}
      {!isAdmin && companyData?.industries && companyData.industries.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Factory size={16} className="text-reevol-text-muted" />
            <h2 className="text-sm font-semibold text-reevol-text">{t.settings.companyIndustries}</h2>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {companyData.industries.map((ind) => (
              <Badge key={ind} variant="info">{ind}</Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Team Members */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-reevol-text-muted" />
            <h2 className="text-sm font-semibold text-reevol-text">{t.settings.team}</h2>
          </div>
          {team && (
            <span className="text-xs text-reevol-text-muted">{team.length} {t.settings.members}</span>
          )}
        </div>

        {teamLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-10 bg-reevol-elevated rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {team?.map((member) =>
              editingUserId === member.id ? (
                <div key={member.id} className="py-2 px-3 border border-reevol-indigo/30 rounded-xl bg-reevol-indigo/5 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Full name"
                      className="flex-1 px-2 py-1.5 text-sm border border-reevol-border rounded-lg focus:outline-none focus:ring-2 focus:ring-reevol-indigo/30"
                      autoFocus
                    />
                    <input
                      type="tel"
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                      placeholder="Phone"
                      className="w-32 px-2 py-1.5 text-sm border border-reevol-border rounded-lg focus:outline-none focus:ring-2 focus:ring-reevol-indigo/30"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-reevol-text-muted">Role:</span>
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        disabled={member.id === user?.id}
                        className="px-2 py-1 text-xs border border-reevol-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-reevol-indigo/30 disabled:opacity-50"
                      >
                        <option value="admin">Admin</option>
                        <option value="rep">Rep</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={handleSaveUser}
                        disabled={updateUser.isPending}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => setEditingUserId(null)}
                        className="p-1.5 text-reevol-text-muted hover:bg-reevol-elevated rounded-lg transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={member.id} className="flex items-center justify-between py-2 border-b border-reevol-border/50 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-reevol-indigo/10 flex items-center justify-center text-xs font-bold text-reevol-indigo">
                      {member.fullName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-reevol-text">
                        {member.fullName}
                        {member.id === user?.id && <span className="text-reevol-text-muted ml-1">({t.settings.you})</span>}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-reevol-text-muted">
                        <span className="flex items-center gap-0.5">
                          <Mail size={10} />
                          {member.email}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {member.role === 'admin' ? (
                      <Badge variant="info">
                        <Shield size={10} className="mr-0.5" />
                        {t.settings.admin}
                      </Badge>
                    ) : (
                      <Badge variant="neutral">{t.settings.rep}</Badge>
                    )}
                    {member.isSuperAdmin && (
                      <Badge variant="info">
                        <Shield size={10} className="mr-0.5" />
                        Platform
                      </Badge>
                    )}
                    {isAdmin && member.id !== user?.id && (
                      <>
                        {user?.isSuperAdmin && (
                          <button
                            onClick={() => {
                              const action = member.isSuperAdmin ? 'remove platform admin from' : 'grant platform admin to';
                              if (window.confirm(`${action} ${member.fullName}?`)) {
                                toggleSuperAdmin.mutate({ userId: member.id, isSuperAdmin: !member.isSuperAdmin });
                              }
                            }}
                            className={`p-1 rounded-lg transition-colors ${
                              member.isSuperAdmin
                                ? 'text-reevol-indigo hover:text-red-600 hover:bg-red-50'
                                : 'text-reevol-text-muted hover:text-reevol-indigo hover:bg-reevol-indigo/10'
                            }`}
                            title={member.isSuperAdmin ? 'Remove platform admin' : 'Grant platform admin'}
                          >
                            <Shield size={13} />
                          </button>
                        )}
                        <button
                          onClick={() => startEditUser(member)}
                          className="p-1 rounded-lg text-reevol-text-muted hover:text-reevol-indigo hover:bg-reevol-indigo/10 transition-colors"
                          title="Edit user"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => handleResetPassword(member.id, member.email)}
                          className={`p-1 rounded-lg transition-colors ${
                            resetSentId === member.id
                              ? 'text-emerald-600'
                              : 'text-reevol-text-muted hover:text-reevol-indigo hover:bg-reevol-indigo/10'
                          }`}
                          title="Send password reset"
                        >
                          {resetSentId === member.id ? <Check size={13} /> : <KeyRound size={13} />}
                        </button>
                        <button
                          onClick={() => handleRemoveUser(member.id, member.fullName)}
                          disabled={removeUser.isPending}
                          className="p-1 rounded-lg text-reevol-text-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                          title={t.settings.removeMember}
                        >
                          <X size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </Card>

      {/* Invite Team */}
      {isAdmin && (
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-reevol-text mb-2">{t.settings.inviteTeam}</h2>
          <p className="text-xs text-reevol-text-muted mb-3">
            {t.settings.inviteDesc}
          </p>
          <div className="flex gap-2">
            <Input
              placeholder={t.settings.invitePlaceholder}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <Button
              onClick={handleSendInvite}
              loading={inviteUser.isPending}
              disabled={!inviteEmail.trim()}
              icon={<Send size={14} />}
            >
              {t.settings.send}
            </Button>
          </div>
          {inviteSent && (
            <p className="text-xs text-emerald-600 mt-2">{t.settings.inviteSent}</p>
          )}
          {inviteUser.isError && (
            <p className="text-xs text-red-600 mt-2">
              {(inviteUser.error as Error)?.message || t.settings.failedInvite}
            </p>
          )}

          {/* Pending invitations */}
          {invitations && invitations.length > 0 && (
            <div className="mt-4 pt-3 border-t border-reevol-border/50">
              <p className="text-xs font-medium text-reevol-text-muted mb-2 flex items-center gap-1">
                <Clock size={10} />
                {t.settings.pendingInvitations}
              </p>
              <div className="space-y-2">
                {invitations.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between py-1.5">
                    <div>
                      <p className="text-sm text-reevol-text">{inv.email}</p>
                      <p className="text-[10px] text-reevol-text-muted">
                        {inv.expired ? t.common.expired : `${t.common.expires} ${new Date(inv.expiresAt).toLocaleDateString()}`}
                        {` · ${t.common.by} `}{inv.invitedBy}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleResendInvite(inv.id)}
                        disabled={resendInvitation.isPending}
                        className={`p-1 rounded-lg transition-colors ${
                          resentInviteId === inv.id
                            ? 'text-emerald-600'
                            : 'text-reevol-text-muted hover:text-reevol-indigo hover:bg-reevol-indigo/10'
                        }`}
                        title="Resend invitation"
                      >
                        {resentInviteId === inv.id ? <Check size={14} /> : <RefreshCw size={13} />}
                      </button>
                      <button
                        onClick={() => handleRevokeInvite(inv.id, inv.email)}
                        disabled={revokeInvitation.isPending}
                        className="p-1 rounded-lg text-reevol-text-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                        title={t.settings.revokeInvitation}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Language */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-reevol-text mb-1">{t.settings.language}</h2>
        <p className="text-xs text-reevol-text-muted mb-3">{t.settings.languageDesc}</p>
        <div className="flex gap-2">
          {(['en', 'zh'] as Lang[]).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                lang === l
                  ? 'bg-reevol-indigo/10 text-reevol-indigo border border-reevol-indigo/30'
                  : 'bg-reevol-elevated text-reevol-text-secondary border border-reevol-border hover:border-reevol-border-hover'
              }`}
            >
              {t.lang[l]}
            </button>
          ))}
        </div>
      </Card>

      {/* Sign Out */}
      <Button variant="danger" fullWidth onClick={handleLogout} icon={<LogOut size={16} />}>
        {t.settings.signOut}
      </Button>

      <p className="text-xs text-reevol-text-muted text-center">{t.common.version}</p>
    </div>
  );
}
